import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { waLink } from "@/siteConfig";
import { useLang } from "@/i18n";

const NOW = ["b.now.mum", "b.now.blr", "b.now.del", "b.now.gulf", "b.now.us", "b.now.uk", "b.now.other"];
const WHAT = ["b.what.vac", "b.what.farm", "b.what.coast", "b.what.pent", "b.what.else"];
const BUDGET = ["b.budget.u1", "b.budget.13", "b.budget.310", "b.budget.10", "b.budget.unsure"];
const WHEN = ["b.when.exp", "b.when.yr", "b.when.now"];

export const BuyerForm = ({ intent, locality }) => {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [now, setNow] = useState("");
  const [what, setWhat] = useState(intent || "");
  const [budget, setBudget] = useState("");
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (intent) setWhat(intent);
  }, [intent]);

  const missing = { name: !name.trim(), phone: !phone.trim(), now: !now, what: !what };
  const err = (k) => attempted && missing[k];

  const lines = [
    t("wa.buyer.title"),
    "—",
    `${t("wa.name")}: ${name.trim()}`,
    `${t("wa.phone")}: ${phone.trim()}`,
    `${t("wa.now")}: ${t(now)}`,
    `${t("wa.looking")}: ${t(what)}`,
  ];
  if (locality) lines.push(`${t("wa.locality")}: Bhubaneswar — ${locality}`);
  if (budget) lines.push(`${t("wa.budget")}: ${t(budget)}`);
  if (when) lines.push(`${t("wa.when")}: ${t(when)}`);
  if (notes.trim()) lines.push(`${t("wa.notes")}: ${notes.trim()}`);
  const url = waLink(lines.join("\n"));

  const selectCls = (error) =>
    `w-full bg-[#0E1613] border px-4 py-3 text-sm font-light outline-none transition-colors duration-300 ${
      error ? "border-[#B8894B] text-[#B8894B]" : "border-[#1A2A24] focus:border-[#B8894B] text-[#EDE7DA]"
    }`;
  const lineCls = (error) =>
    `w-full bg-transparent border-b outline-none py-2.5 text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300 ${
      error ? "border-[#B8894B]" : "border-[#1A2A24] focus:border-[#B8894B]"
    }`;

  const Label = ({ children, error }) => (
    <span
      className={`font-mono-label text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${
        error ? "text-[#B8894B]" : "text-[#EDE7DA]/45"
      }`}
    >
      {children}
      {error ? ` ${t("lc.required")}` : ""}
    </span>
  );

  const submit = () => {
    setAttempted(true);
    if (Object.values(missing).some(Boolean)) return;
    window.open(url, "_blank");
    setSent(true);
  };

  if (sent) {
    return (
      <div data-testid="buyer-form-confirmation" className="py-4">
        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#B8894B]">
          <Check className="w-5 h-5 text-[#B8894B]" />
        </span>
        <h3 className="font-display text-2xl text-[#EDE7DA] tracking-tight mt-6">
          {t("bf.done.h")}
        </h3>
        <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
          {t("bf.done.body")}
        </p>
        <a
          data-testid="buyer-form-fallback-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-7 font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#B8894B] border-b border-[#B8894B]/40 pb-1 hover:text-[#EDE7DA] hover:border-[#EDE7DA]/40 transition-colors duration-300"
        >
          {t("lc.done.fallback")}
        </a>
      </div>
    );
  }

  return (
    <div className="grid gap-7 mt-8">
      {locality && (
        <p
          data-testid="buyer-locality-tag"
          className="w-fit font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#B8894B] border border-[#B8894B]/50 bg-[#1A2A24] px-4 py-2.5"
        >
          Bhubaneswar — {locality}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <Label error={err("name")}>{t("bf.name")}</Label>
          <input
            data-testid="buyer-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("ph.name")}
            className={`mt-2 ${lineCls(err("name"))}`}
          />
        </div>
        <div>
          <Label error={err("phone")}>{t("bf.phone")}</Label>
          <input
            data-testid="buyer-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 / +971 / +91"
            className={`mt-2 ${lineCls(err("phone"))}`}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <Label error={err("now")}>{t("bf.now")}</Label>
          <div className="mt-2">
            <select
              data-testid="buyer-now-select"
              value={now}
              onChange={(e) => setNow(e.target.value)}
              className={selectCls(err("now"))}
            >
              <option value="" disabled>
                {t("lc.selectPh")}
              </option>
              {NOW.map((k) => (
                <option key={k} value={k}>
                  {t(k)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Label error={err("what")}>{t("bf.what")}</Label>
          <div className="mt-2">
            <select
              data-testid="buyer-what-select"
              value={what}
              onChange={(e) => setWhat(e.target.value)}
              className={selectCls(err("what"))}
            >
              <option value="" disabled>
                {t("lc.selectPh")}
              </option>
              {WHAT.map((k) => (
                <option key={k} value={k}>
                  {t(k)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <Label>{t("bf.budget")}</Label>
          <div className="mt-2">
            <select
              data-testid="buyer-budget-select"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className={selectCls(false)}
            >
              <option value="">{t("lc.selectPh")}</option>
              {BUDGET.map((k) => (
                <option key={k} value={k}>
                  {t(k)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Label>{t("bf.when")}</Label>
          <div className="mt-2">
            <select
              data-testid="buyer-when-select"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className={selectCls(false)}
            >
              <option value="">{t("lc.selectPh")}</option>
              {WHEN.map((k) => (
                <option key={k} value={k}>
                  {t(k)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <Label>{t("bf.notes")}</Label>
        <textarea
          data-testid="buyer-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("lc.notesPh")}
          rows={3}
          className="mt-2 w-full bg-transparent border border-[#1A2A24] focus:border-[#B8894B] outline-none p-4 text-sm text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300 resize-none"
        />
      </div>

      <div>
        <button
          type="button"
          data-testid="buyer-form-submit"
          onClick={submit}
          className="w-full font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
        >
          {t("bf.submit")}
        </button>
        <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-4 text-center">
          {t("cta.waNote")}
        </p>
      </div>
    </div>
  );
};
